// services/astro-bot-miniapp/src/screens/onboarding/PersonalReveal.tsx
// Wave E (Phase 7)
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api-client.js';
import { useAuth } from '../../store/auth.js';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OnboardingProgressBar } from '../../components/onboarding/OnboardingProgressBar.js';
import {
  PersonaCarousel,
  type PersonaCarouselLabels,
  type PersonaFrame,
} from '../../components/onboarding/index.js';
import styles from './PersonalReveal.module.css';

interface Frame { r2Key: string; url: string; archetype?: string }
type SelfieStatus = 'absent' | 'pending' | 'ready' | 'failed';
type SelfieErrorCode = 'daily_cap' | 'global_cap' | 'generation_failed';
interface Resp { selfie: { status: SelfieStatus; frames: Frame[]; errorCode?: SelfieErrorCode | null } }

export function PersonalReveal(): JSX.Element {
  const { t } = useTranslation('flows');
  const { t: tc } = useTranslation('common');
  const jwt = useAuth(s => s.jwt);
  const setStep = useAuth(s => s.setOnboardingStep);
  const nav = useNavigate();
  const [frames, setFrames] = useState<Frame[]>([]);
  const [active, setActive] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [errorCode, setErrorCode] = useState<SelfieErrorCode | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;
    setLoadFailed(false);

    const tick = async (): Promise<void> => {
      try {
        const r = await apiFetch('/api/v1/star-persona', jwt);
        if (!r.ok) {
          console.error('[PersonalReveal] fetch failed', r.status);
          if (!cancelled) setLoadFailed(true);
          return;
        }
        const j = (await r.json()) as Resp;
        if (cancelled) return;

        // If user never submitted a selfie, they're on the wrong screen — go back to picker.
        if (j.selfie.status === 'absent') {
          nav('/onboarding/pick', { replace: true });
          return;
        }
        // If the selfie pack failed, surface a retry/back affordance instead of generic fallback.
        if (j.selfie.status === 'failed') {
          if (!cancelled) {
            setErrorCode(j.selfie.errorCode ?? 'generation_failed');
            setLoadFailed(true);
          }
          return;
        }
        // While selfie is being generated, do NOT show generic archetype previews.
        // Bounce the user back to the loader screen which already polls.
        if (j.selfie.status === 'pending' || (j.selfie.frames?.length ?? 0) === 0) {
          nav('/onboarding/adding', { replace: true });
          return;
        }
        setFrames((j.selfie.frames ?? []).slice(0, 9));
      } catch (err) {
        console.error('[PersonalReveal] fetch error', err);
        if (!cancelled) setLoadFailed(true);
      }
    };

    void tick();
    return () => { cancelled = true; };
  }, [jwt, reloadTick, nav]);

  async function pick(): Promise<void> {
    if (!jwt || submitting || frames.length === 0) return;
    setSubmitting(true);
    setError(false);
    try {
      const r = await apiFetch('/api/v1/star-persona/select', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ r2Key: frames[active].r2Key }),
      });
      if (!r.ok) {
        setError(true);
        return;
      }
      const j = (await r.json()) as { onboardingStep?: number };
      if (typeof j.onboardingStep === 'number') setStep(j.onboardingStep);
      nav('/onboarding/push', { replace: true });
    } catch (err) {
      console.error('[PersonalReveal] select error', err);
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }

  const carouselFrames: PersonaFrame[] = useMemo(
    () => frames.map((f) => ({ r2Key: f.r2Key, url: f.url, label: f.archetype })),
    [frames],
  );

  const labels: PersonaCarouselLabels = useMemo(
    () => ({
      overlay: t('onboarding.personalReveal.overlay'),
      ctaMinipack: t('onboarding.personalReveal.cta.useAsProfile'),
    }),
    [t],
  );

  if (loadFailed && frames.length === 0) {
    const messageKey = errorCode
      ? `onboarding.personalReveal.errors.${errorCode}`
      : 'onboarding.personalReveal.error';
    return (
      <div className={styles.errorScreen}>
        <CosmosBackdrop />
        <p className={styles.errorText} role="alert">
          {t(messageKey, { defaultValue: t('onboarding.personalReveal.error') })}
        </p>
        <button
          type="button"
          className={styles.cta}
          onClick={() => {
            setErrorCode(null);
            setReloadTick(tick => tick + 1);
          }}
        >
          {t('onboarding.computing.retry', { defaultValue: 'Retry' })}
        </button>
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={() => nav('/onboarding/pick', { replace: true })}
        >
          {t('onboarding.personalReveal.cta.skip')}
        </button>
      </div>
    );
  }

  if (frames.length === 0) {
    return <div className={styles.loading}>{tc('states.loading')}</div>;
  }

  const archetype = (frames[0]?.archetype ?? '').replace(/_/g, ' ');

  return (
    <div className={styles.screen}>
      <CosmosBackdrop />
      <OnboardingProgressBar step={6} etaSeconds={10} />
      <h1 className={styles.title}>{t('onboarding.personalReveal.title')}</h1>
      <p className={styles.subtitle}>{t('onboarding.personalReveal.subtitle', { archetype })}</p>

      <PersonaCarousel
        frames={carouselFrames}
        tier="mini"
        labels={labels}
        selectedIndex={active}
        onSelectFrame={setActive}
        onCtaPress={() => void pick()}
      />

      {error && (
        <p className={styles.errorText} role="alert">{t('onboarding.personalReveal.error')}</p>
      )}

      <button
        type="button"
        className={styles.ctaSecondary}
        onClick={() => nav('/onboarding/push', { replace: true })}
        disabled={submitting}
      >
        {t('onboarding.personalReveal.cta.skip')}
      </button>
    </div>
  );
}
